import { z } from "zod";
import { apiService, organizationIdHeader } from "../api.service";
import { type AuditLogDisplay, AuditLogDisplaySchema } from "../job/job.types";

const AuditLogArray = z.array(AuditLogDisplaySchema);

import {
	type CheckEmailUsage,
	CheckEmailUsageSchema,
	type CreateOrganizationInfo,
	CreateOrganizationInfoSchema,
	type CreateOrganizationUserInfo,
	CreateOrganizationUserInfoSchema,
	type CreateSubContractorInfo,
	CreateSubContractorInfoSchema,
	type CreateSubOrganizationInfo,
	CreateSubOrganizationInfoSchema,
	type DataAccessDisplay,
	DataAccessDisplaySchema,
	type DataAccessFilters,
	type DataAccessInfo,
	DataAccessInfoSchema,
	type GetOrganizationStatusesResponse,
	type GlobalRole,
	GlobalRoleSchema,
	type OAuthProvider,
	OAuthProviderSchema,
	type Organization,
	OrganizationApiError,
	type OrganizationContact,
	OrganizationContactSchema,
	type OrganizationParams,
	type OrganizationRoleInfo,
	OrganizationRoleInfoSchema,
	type OrganizationRoleListItem,
	OrganizationRoleListItemSchema,
	OrganizationSchema,
	OrganizationStatusItemSchema,
	type OrganizationUserDisplay,
	OrganizationUserDisplaySchema,
	type OrganizationUsersParams,
	type PaginationParams,
	type Permission,
	PermissionSchema,
	type ServiceResult,
	ServiceResultSchema,
	type SubContractorDisplay,
	SubContractorDisplaySchema,
	type SubContractorListItem,
	SubContractorListItemSchema,
	type SubOrganizationDisplay,
	SubOrganizationDisplaySchema,
	SubOrganizationDisplaysArraySchema,
	type SystemOrganizationRole,
	SystemOrganizationRoleSchema,
	type UpdateOrganizationContactInfo,
	UpdateOrganizationContactInfoSchema,
	type UpdateOrganizationInfo,
	UpdateOrganizationInfoSchema,
	type UpdateOrganizationStatus,
	UpdateOrganizationStatusSchema,
	type UpdateOrganizationUserInfo,
	UpdateOrganizationUserInfoSchema,
	type UpdateOrganizationUserStatus,
	UpdateOrganizationUserStatusSchema,
	type UpdateSubContractorInfo,
	UpdateSubContractorInfoSchema,
	type UpdateSubOrganizationContactInfo,
	UpdateSubOrganizationContactInfoSchema,
} from "./organization.types";

// Array schemas for list responses
const OrganizationsArray = z.array(OrganizationSchema);
const GlobalRolesArray = z.array(GlobalRoleSchema);
const OrganizationRoleListArray = z.array(OrganizationRoleListItemSchema);
const SystemOrganizationRolesArray = z.array(SystemOrganizationRoleSchema);
const OrganizationContactsArray = z.array(OrganizationContactSchema);
const OrganizationUserDisplaysArray = z.array(OrganizationUserDisplaySchema);
const SubContractorListItemsArray = z.array(SubContractorListItemSchema);
const DataAccessDisplaysArray = z.array(DataAccessDisplaySchema);
const DataAccessInfosArray = z.array(DataAccessInfoSchema);
const OrganizationStatusesArraySchema = z.array(OrganizationStatusItemSchema);

export class OrganizationService {
	// ==================== CORE ORGANIZATION OPERATIONS ====================

	/**
	 * Get organizations with optional filtering and pagination
	 */
	async getOrganizations(
		start: number = 1,
		count: number = 10,
		params?: OrganizationParams,
	): Promise<Organization[]> {
		try {
			const response = await apiService.get<Organization[]>(
				`/organizations/${start}/${count}`,
				{ params },
			);

			return OrganizationsArray.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get organizations: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get organizations count with optional filtering
	 */
	async getOrganizationsCount(params?: OrganizationParams): Promise<number> {
		try {
			const response = await apiService.get<number>("/organizations/count", {
				params,
			});

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get organizations count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get a single organization by ID
	 */
	async getOrganization(id: string): Promise<Organization> {
		try {
			const response = await apiService.get<Organization>(
				`/organizations/${id}`,
			);

			// return OrganizationSchema.parse(response);
			return response;
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get organization ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a new organization with enhanced validation and error handling
	 */
	async createOrganization(
		data: CreateOrganizationInfo,
	): Promise<ServiceResult> {
		try {
			// Enhanced validation with detailed error messages
			const validatedData = CreateOrganizationInfoSchema.parse(data);

			const response = await apiService.post<ServiceResult>("/organizations", {
				name: validatedData.name,
			});

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw OrganizationApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			console.error(error);

			if (error instanceof OrganizationApiError) {
				throw error;
			}

			// Enhanced error handling with specific error types
			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new OrganizationApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}

			throw new OrganizationApiError(
				`Failed to create organization: ${error instanceof Error ? error : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update an existing organization
	 */
	async updateOrganization(
		id: string,
		data: UpdateOrganizationInfo,
	): Promise<void> {
		try {
			const validatedData = UpdateOrganizationInfoSchema.parse(data);
			await apiService.put(`/organizations/${id}`, validatedData);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to update organization ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update organization status
	 */
	async updateOrganizationStatus(
		id: string,
		data: UpdateOrganizationStatus,
	): Promise<void> {
		try {
			const validatedData = UpdateOrganizationStatusSchema.parse(data);

			await apiService.put(`/organizations/${id}/status`, validatedData);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to update organization status ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== ORGANIZATION ROLES OPERATIONS ====================

	/**
	 * Get organization roles count
	 */
	async getOrganizationRolesCount(
		organizationId: string,
		params?: OrganizationParams,
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/organizations/${organizationId}/roles/count`,
				{ params },
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get organization roles count for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get all roles for an organization
	 */
	async getOrganizationRoles(
		organizationId: string,
		params: PaginationParams = { start: 1, count: 10 },
	): Promise<OrganizationRoleListItem[]> {
		try {
			const response = await apiService.get<OrganizationRoleListItem[]>(
				`/organizations/${organizationId}/roles/${params.start}/${params.count}`,
			);

			return OrganizationRoleListArray.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get organization roles for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get organization roles count
	 */
	async getSystemOrganizationRolesCount(
		organizationId: string,
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/organizations/${organizationId}/roles/count`,
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get organization roles count for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get organization roles with pagination
	 */
	async getOrganizationRolesPaginated(
		organizationId: string,
		start: number = 1,
		count: number = 10,
	): Promise<SystemOrganizationRole[]> {
		try {
			const response = await apiService.get<SystemOrganizationRole[]>(
				`/organizations/${organizationId}/roles/${start}/${count}`,
			);

			return SystemOrganizationRolesArray.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get organization roles for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Permissions that can be assigned to an organization role.
	 * GET /organization-roles/permissions
	 */
	async getOrganizationRolePermissions(): Promise<Permission[]> {
		try {
			const response = await apiService.get<Permission[]>(
				"/organization-roles/permissions",
			);
			return z.array(PermissionSchema).parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get organization role permissions: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a new role for an organization
	 */
	async createOrganizationRole(
		organizationId: string,
		data: OrganizationRoleInfo,
	): Promise<void> {
		try {
			const validatedData = OrganizationRoleInfoSchema.parse(data);

			await apiService.post(
				`/organizations/${organizationId}/roles`,
				validatedData,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to create organization role for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update an existing organization role
	 */
	async updateOrganizationRole(
		organizationId: string,
		roleId: string,
		data: OrganizationRoleInfo,
	): Promise<void> {
		try {
			const validatedData = OrganizationRoleInfoSchema.parse(data);

			await apiService.put(
				`/organizations/${organizationId}/roles/${roleId}`,
				validatedData,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to update organization role ${roleId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete an organization role
	 */
	async deleteOrganizationRole(
		organizationId: string,
		roleId: string,
	): Promise<void> {
		try {
			await apiService.delete(
				`/organizations/${organizationId}/roles/${roleId}`,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to delete organization role ${roleId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== ORGANIZATION CONTACTS OPERATIONS ====================

	/**
	 * Get all contacts for an organization
	 */
	async getOrganizationContacts(
		organizationId: string,
	): Promise<OrganizationContact[]> {
		try {
			const response = await apiService.get<OrganizationContact[]>(
				`/organizations/${organizationId}/contacts`,
			);

			console.log("response", response);

			return OrganizationContactsArray.parse(response);
		} catch (error) {
			console.log("error", error);

			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get organization contacts for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update an organization contact
	 */
	async updateOrganizationContact(
		organizationId: string,
		contactId: string,
		data: UpdateOrganizationContactInfo,
	): Promise<void> {
		try {
			const validatedData = UpdateOrganizationContactInfoSchema.parse(data);

			await apiService.put(
				`/organizations/${organizationId}/contacts/${contactId}`,
				validatedData,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to update organization contact ${contactId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== ORGANIZATION OAUTH OPERATIONS ====================

	/**
	 * Get OAuth provider information for an organization
	 */
	async getOrganizationOAuthInfo(
		organizationId: string,
	): Promise<OAuthProvider> {
		try {
			const response = await apiService.get<OAuthProvider>(
				`/organizations/${organizationId}/oauth-info`,
			);

			return OAuthProviderSchema.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get OAuth info for organization ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update OAuth provider information for an organization
	 */
	async updateOrganizationOAuthInfo(
		organizationId: string,
		data: OAuthProvider,
	): Promise<void> {
		try {
			const validatedData = OAuthProviderSchema.parse(data);

			await apiService.put(
				`/organizations/${organizationId}/oauth-info`,
				validatedData,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to update OAuth info for organization ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== ORGANIZATION USERS OPERATIONS ====================

	/**
	 * Add a user to an organization
	 */
	async addOrganizationUser(
		organizationId: string,
		data: CreateOrganizationUserInfo,
	): Promise<void> {
		try {
			const validatedData = CreateOrganizationUserInfoSchema.parse(data);

			// API requires ALL fields to be present (even if null)
			// Do NOT strip out null/empty values - the API validation expects these fields
			await apiService.post(
				`/organizations/${organizationId}/users`,
				validatedData,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to add user to organization ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Bulk create organization users
	 * POST /organizations/{organizationId}/users/bulk
	 *
	 * @param organizationId - The organization ID
	 * @param users - Array of user creation data
	 * @returns ServiceResult with success status
	 */
	async bulkCreateOrganizationUsers(
		organizationId: string,
		users: CreateOrganizationUserInfo[],
	): Promise<ServiceResult> {
		try {
			// Validate each user in the array
			const validatedUsers = users.map((user) =>
				CreateOrganizationUserInfoSchema.parse(user),
			);

			const response = await apiService.post<ServiceResult>(
				`/organizations/${organizationId}/users/bulk`,
				validatedUsers,
			);

			return ServiceResultSchema.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new OrganizationApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}
			throw new OrganizationApiError(
				`Failed to bulk create users for organization ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get a specific organization user
	 * @param organizationId - The organization ID
	 * @param organizationUserId - The organization user ID (join table ID, not platform user ID)
	 * @returns OrganizationUserDisplay with all user details including roles and related entities
	 */
	async getOrganizationUser(
		organizationId: string,
		organizationUserId: string,
	): Promise<OrganizationUserDisplay> {
		try {
			const response = await apiService.get<OrganizationUserDisplay>(
				`/organizations/${organizationId}/users/${organizationUserId}`,
			);

			return OrganizationUserDisplaySchema.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get organization user ${organizationUserId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update an organization user
	 * @param organizationId - The organization ID
	 * @param organizationUserId - The organization user ID (join table ID, not platform user ID)
	 * @param data - The update data
	 * @returns ServiceResult with success status and affectedId
	 */
	async updateOrganizationUser(
		organizationId: string,
		organizationUserId: string,
		data: UpdateOrganizationUserInfo,
	): Promise<ServiceResult> {
		try {
			const validatedData = UpdateOrganizationUserInfoSchema.parse(data);

			const response = await apiService.put<ServiceResult>(
				`/organizations/${organizationId}/users/${organizationUserId}`,
				validatedData,
			);

			return ServiceResultSchema.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to update organization user ${organizationUserId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Whether a Belongs To change must acknowledge open assignments being removed.
	 * POST /organizations/{organizationId}/users/{organizationUserId}/check-for-potential-changed-assignments
	 * Body is UpdateOrganizationUserInfo. subOrganizationId or subContractorId is set.
	 * true means acknowledgement is required.
	 */
	async checkForPotentialChangedAssignments(
		organizationId: string,
		organizationUserId: string,
		data: UpdateOrganizationUserInfo,
	): Promise<boolean> {
		try {
			const validatedData = UpdateOrganizationUserInfoSchema.parse(data);
			const response = await apiService.post<boolean>(
				`/organizations/${organizationId}/users/${organizationUserId}/check-for-potential-changed-assignments`,
				validatedData,
			);
			return z.boolean().parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to check assignments for organization user ${organizationUserId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update organization user status
	 * @param organizationId - The organization ID
	 * @param organizationUserId - The organization user ID (join table ID, not platform user ID)
	 * @param data - The status update data
	 */
	async updateOrganizationUserStatus(
		organizationId: string,
		organizationUserId: string,
		data: UpdateOrganizationUserStatus,
	): Promise<void> {
		try {
			const validatedData = UpdateOrganizationUserStatusSchema.parse(data);

			await apiService.put(
				`/organizations/${organizationId}/users/${organizationUserId}/status`,
				validatedData,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to update organization user status ${organizationUserId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Force password reset for an organization user (org-admin action)
	 * PUT /organizations/{organizationId}/users/{organizationUserId}/password-reset
	 *
	 * @param organizationId - Organization UUID
	 * @param organizationUserId - Organization user (membership) UUID, not platform user id
	 */
	async forceOrganizationUserPasswordReset(
		organizationId: string,
		organizationUserId: string,
	): Promise<void> {
		try {
			await apiService.put(
				`/organizations/${organizationId}/users/${organizationUserId}/password-reset`,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to force password reset for organization user ${organizationUserId} in ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get organization users count with optional filtering
	 */
	async getOrganizationUsersCount(
		organizationId: string,
		params: OrganizationUsersParams = {},
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/organizations/${organizationId}/users/count`,
				{ params },
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get organization users count for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get organization users with pagination and optional filtering
	 */
	async getOrganizationUsers(
		organizationId: string,
		start: number,
		count: number,
		params: OrganizationUsersParams = {},
	): Promise<OrganizationUserDisplay[]> {
		try {
			const response = await apiService.get<OrganizationUserDisplay[]>(
				`/organizations/${organizationId}/users/${start}/${count}`,
				{ params },
			);
			return OrganizationUserDisplaysArray.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get organization users for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Export organization users to CSV
	 * GET /organizations/{organizationId}/users/{start}/{count}/export
	 */
	async exportOrganizationUsers(
		organizationId: string,
		start: number,
		count: number,
		params?: OrganizationUsersParams & { columns?: string },
	): Promise<Blob> {
		try {
			const response = await apiService.get<Blob>(
				`/organizations/${organizationId}/users/${start}/${count}/export`,
				{
					params,
					headers: {
						Accept: "text/csv",
					},
					responseType: "blob",
				},
			);
			return response;
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to export organization users for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== SUB-ORGANIZATIONS OPERATIONS ====================

	/**
	 * Get sub-organizations count for an organization
	 */
	async getSubOrganizationsCount(
		organizationId: string,
		params?: OrganizationParams,
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/organizations/${organizationId}/sub-organizations/count`,
				{ params },
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get sub-organizations count for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get sub-organizations with pagination and optional filtering
	 */
	async getSubOrganizations(
		organizationId: string,
		start: number,
		count: number,
		params?: OrganizationParams,
	): Promise<SubOrganizationDisplay[]> {
		try {
			const response = await apiService.get<SubOrganizationDisplay[]>(
				`/organizations/${organizationId}/sub-organizations/${start}/${count}`,
				{
					params,
					headers: organizationIdHeader(organizationId),
				},
			);

			return SubOrganizationDisplaysArraySchema.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get sub-organizations for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a new sub-organization
	 * POST /organizations/{organizationId}/sub-organizations
	 *
	 * @param organizationId - The parent organization ID
	 * @param data - The sub-organization creation data
	 * @returns ServiceResult with success status
	 */
	async createSubOrganization(
		organizationId: string,
		data: CreateSubOrganizationInfo,
	): Promise<ServiceResult> {
		try {
			const validatedData = CreateSubOrganizationInfoSchema.parse(data);

			const response = await apiService.post<ServiceResult>(
				`/organizations/${organizationId}/sub-organizations`,
				validatedData,
			);

			return ServiceResultSchema.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new OrganizationApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}
			throw new OrganizationApiError(
				`Failed to create sub-organization for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update an existing sub-organization
	 * PUT /organizations/{organizationId}/sub-organizations/{subOrganizationId}
	 *
	 * @param organizationId - The parent organization ID
	 * @param subOrganizationId - The sub-organization ID to update
	 * @param data - The update data
	 */
	async updateSubOrganization(
		organizationId: string,
		subOrganizationId: string,
		data: UpdateSubOrganizationContactInfo,
	): Promise<void> {
		try {
			const validatedData = UpdateSubOrganizationContactInfoSchema.parse(data);

			await apiService.put(
				`/organizations/${organizationId}/sub-organizations/${subOrganizationId}`,
				validatedData,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new OrganizationApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}
			throw new OrganizationApiError(
				`Failed to update sub-organization ${subOrganizationId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /organizations/{organizationId}/sub-organizations/{subOrganizationId}/close
	 */
	async closeSubOrganization(
		organizationId: string,
		subOrganizationId: string,
	): Promise<void> {
		try {
			await apiService.put(
				`/organizations/${organizationId}/sub-organizations/${subOrganizationId}/close`,
				{},
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to close sub-organization ${subOrganizationId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /organizations/{organizationId}/sub-contractors/{subContractorId}/close
	 */
	async closeSubContractor(
		organizationId: string,
		subContractorId: string,
	): Promise<void> {
		try {
			await apiService.put(
				`/organizations/${organizationId}/sub-contractors/${subContractorId}/close`,
				{},
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to close sub-contractor ${subContractorId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /organizations/{organizationId}/sub-contractors/{subContractorId}/activate
	 */
	async activateSubContractor(
		organizationId: string,
		subContractorId: string,
	): Promise<void> {
		try {
			await apiService.put(
				`/organizations/${organizationId}/sub-contractors/${subContractorId}/activate`,
				{},
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to activate sub-contractor ${subContractorId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * DELETE /organizations/{organizationId}/sub-contractors/{subContractorId}
	 */
	async deleteSubContractor(
		organizationId: string,
		subContractorId: string,
	): Promise<void> {
		try {
			await apiService.delete(
				`/organizations/${organizationId}/sub-contractors/${subContractorId}`,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to delete sub-contractor ${subContractorId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /organizations/{organizationId}/sub-organizations/{subOrganizationId}/activate
	 */
	async activateSubOrganization(
		organizationId: string,
		subOrganizationId: string,
	): Promise<void> {
		try {
			await apiService.put(
				`/organizations/${organizationId}/sub-organizations/${subOrganizationId}/activate`,
				{},
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to activate sub-organization ${subOrganizationId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * DELETE /organizations/{organizationId}/sub-organizations/{subOrganizationId}
	 */
	async deleteSubOrganization(
		organizationId: string,
		subOrganizationId: string,
	): Promise<void> {
		try {
			await apiService.delete(
				`/organizations/${organizationId}/sub-organizations/${subOrganizationId}`,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to delete sub-organization ${subOrganizationId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Export sub-organizations to CSV
	 * GET /organizations/{organizationId}/sub-organizations/{start}/{count}/export
	 *
	 * @param organizationId - The parent organization ID
	 * @param start - Starting index (1-based)
	 * @param count - Number of sub-organizations to export
	 * @param params - Optional query parameters
	 * @returns Blob containing CSV data
	 */
	async exportSubOrganizations(
		organizationId: string,
		start: number,
		count: number,
		params?: OrganizationParams & { columns?: string },
	): Promise<Blob> {
		try {
			const response = await apiService.get<Blob>(
				`/organizations/${organizationId}/sub-organizations/${start}/${count}/export`,
				{
					params,
					headers: {
						Accept: "text/csv",
					},
					responseType: "blob",
				},
			);
			return response;
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to export sub-organizations for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== SUB-CONTRACTORS OPERATIONS ====================

	/**
	 * Get sub-contractors count for an organization
	 */
	async getSubContractorsCount(
		organizationId: string,
		params?: OrganizationParams,
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/organizations/${organizationId}/sub-contractors/count`,
				{ params },
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get sub-contractors count for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get sub-contractors with pagination and optional filtering
	 * GET /organizations/{organizationId}/sub-contractors/{start}/{count}
	 *
	 * Note: List endpoint returns lightweight SubContractorListItem objects,
	 * not full SubContractorDisplay objects (which include audit fields).
	 */
	async getSubContractors(
		organizationId: string,
		start: number,
		count: number,
		params?: OrganizationParams,
	): Promise<SubContractorListItem[]> {
		try {
			// const params: Record<string, string | number | boolean> = {};

			// if (filters.searchTerm) {
			// 	params.SearchTerm = filters.searchTerm;
			// }

			const response = await apiService.get<SubContractorListItem[]>(
				`/organizations/${organizationId}/sub-contractors/${start}/${count}`,
				{ params },
			);

			return SubContractorListItemsArray.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get sub-contractors for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get a single sub-contractor by ID
	 * GET /organizations/{organizationId}/sub-contractors/{subContractorId}
	 *
	 * @param organizationId - The parent organization ID
	 * @param subContractorId - The sub-contractor ID
	 * @returns SubContractorDisplay object
	 */
	async getSubContractor(
		organizationId: string,
		subContractorId: string,
	): Promise<SubContractorDisplay> {
		try {
			const response = await apiService.get<SubContractorDisplay>(
				`/organizations/${organizationId}/sub-contractors/${subContractorId}`,
			);
			return SubContractorDisplaySchema.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get sub-contractor ${subContractorId} for organization ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a new sub-contractor
	 * POST /organizations/{organizationId}/sub-contractors
	 *
	 * @param organizationId - The parent organization ID
	 * @param data - The sub-contractor creation data
	 * @returns ServiceResult with success status
	 */
	async createSubContractor(
		organizationId: string,
		data: CreateSubContractorInfo,
	): Promise<ServiceResult> {
		try {
			const validatedData = CreateSubContractorInfoSchema.parse(data);

			const response = await apiService.post<ServiceResult>(
				`/organizations/${organizationId}/sub-contractors`,
				validatedData,
			);

			return ServiceResultSchema.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new OrganizationApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}
			throw new OrganizationApiError(
				`Failed to create sub-contractor for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update an existing sub-contractor
	 * PUT /organizations/{organizationId}/sub-contractors/{subContractorId}
	 *
	 * @param organizationId - The parent organization ID
	 * @param subContractorId - The sub-contractor ID to update
	 * @param data - The update data
	 */
	async updateSubContractor(
		organizationId: string,
		subContractorId: string,
		data: UpdateSubContractorInfo,
	): Promise<void> {
		try {
			const validatedData = UpdateSubContractorInfoSchema.parse(data);

			await apiService.put(
				`/organizations/${organizationId}/sub-contractors/${subContractorId}`,
				validatedData,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new OrganizationApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}
			throw new OrganizationApiError(
				`Failed to update sub-contractor ${subContractorId} for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Export sub-contractors to CSV
	 * GET /organizations/{organizationId}/sub-contractors/{start}/{count}/export
	 *
	 * @param organizationId - The parent organization ID
	 * @param start - Starting index (1-based)
	 * @param count - Number of sub-contractors to export
	 * @param params - Optional query parameters
	 * @returns Blob containing CSV data
	 */
	async exportSubContractors(
		organizationId: string,
		start: number,
		count: number,
		params?: OrganizationParams & { columns?: string },
	): Promise<Blob> {
		try {
			const response = await apiService.get<Blob>(
				`/organizations/${organizationId}/sub-contractors/${start}/${count}/export`,
				{
					params,
					headers: {
						Accept: "text/csv",
					},
					responseType: "blob",
				},
			);
			return response;
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to export sub-contractors for ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== SUB-CONTRACTOR SUB-ORGANIZATION ACCESS ====================

	/**
	 * Assign a sub-organization to a sub-contractor
	 * PUT /organizations/{organizationId}/sub-contractors/{subContractorId}/access/{subOrganizationId}
	 */
	async assignSubContractorSubOrgAccess(
		organizationId: string,
		subContractorId: string,
		subOrganizationId: string,
	): Promise<void> {
		try {
			await apiService.put(
				`/organizations/${organizationId}/sub-contractors/${subContractorId}/access/${subOrganizationId}`,
				undefined,
				{ headers: { "organization-id": organizationId } },
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to assign sub-organization ${subOrganizationId} to sub-contractor ${subContractorId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Unassign a sub-organization from a sub-contractor
	 * DELETE /organizations/{organizationId}/sub-contractors/{subContractorId}/access/{subOrganizationId}
	 * A 404 means the assignment does not exist — treat as a no-op.
	 */
	async unassignSubContractorSubOrgAccess(
		organizationId: string,
		subContractorId: string,
		subOrganizationId: string,
	): Promise<void> {
		try {
			await apiService.delete(
				`/organizations/${organizationId}/sub-contractors/${subContractorId}/access/${subOrganizationId}`,
				{ headers: { "organization-id": organizationId } },
			);
		} catch (error) {
			// 404 means the access record does not exist — already unassigned, nothing to do.
			const status = (error as { status?: number })?.status;
			if (status === 404) {
				return;
			}
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to unassign sub-organization ${subOrganizationId} from sub-contractor ${subContractorId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== EMAIL CHECK OPERATIONS ====================

	/**
	 * Check if an email is in use within the organization or globally
	 * GET /organizations/{organizationId}/users/check-email
	 *
	 * @param organizationId - The organization ID
	 * @param email - The email address to check
	 * @returns CheckEmailUsage with inUseGlobally and inUseInThisOrganization flags
	 */
	async checkEmailUsage(
		organizationId: string,
		email: string,
	): Promise<CheckEmailUsage> {
		try {
			const response = await apiService.get<CheckEmailUsage>(
				`/organizations/${organizationId}/users/check-email`,
				{ params: { email } },
			);

			return CheckEmailUsageSchema.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to check email usage for ${email} in organization ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== UTILITY METHODS ====================

	/**
	 * Check if an organization exists
	 */
	async organizationExists(id: string): Promise<boolean> {
		try {
			await this.getOrganization(id);
			return true;
		} catch (error) {
			if (error instanceof OrganizationApiError && error.status === 404) {
				return false;
			}
			throw error;
		}
	}

	/**
	 * Check if a user exists in an organization
	 * @param organizationId - The organization ID
	 * @param organizationUserId - The organization user ID (join table ID, not platform user ID)
	 */
	async organizationUserExists(
		organizationId: string,
		organizationUserId: string,
	): Promise<boolean> {
		try {
			await this.getOrganizationUser(organizationId, organizationUserId);
			return true;
		} catch (error) {
			if (error instanceof OrganizationApiError && error.status === 404) {
				return false;
			}
			throw error;
		}
	}

	/**
	 * Get all organizations (convenience method for unlimited results)
	 */
	async getAllOrganizations(
		params?: OrganizationParams,
	): Promise<Organization[]> {
		const count = await this.getOrganizationsCount(params);
		if (count === 0) {
			return [];
		}
		return this.getOrganizations(1, count, params);
	}

	/**
	 * Get all organization users (convenience method for unlimited results)
	 */
	async getAllOrganizationUsers(
		organizationId: string,
		params: OrganizationUsersParams = {},
	): Promise<OrganizationUserDisplay[]> {
		const count = await this.getOrganizationUsersCount(organizationId, params);
		if (count === 0) {
			return [];
		}
		return this.getOrganizationUsers(organizationId, 1, count, params);
	}

	// ==================== SYSTEM-WIDE ORGANIZATION ROLES ====================

	/**
	 * Get a single organization role by ID (system-wide)
	 * GET /organization-role/{roleId}
	 *
	 * @param roleId - The role's UUID
	 * @returns System organization role object
	 * @throws OrganizationApiError if role not found or API error
	 */
	async getOrganizationRoleById(
		roleId: string,
	): Promise<SystemOrganizationRole> {
		try {
			const response = await apiService.get<SystemOrganizationRole>(
				`/organization-roles/${roleId}`,
			);
			return SystemOrganizationRoleSchema.parse(response);
		} catch (error) {
			console.error("Failed to fetch organization role:", error);
			if (error instanceof z.ZodError) {
				throw new OrganizationApiError(
					"Invalid organization role data received",
					error,
					undefined,
					400,
					error.issues,
				);
			}
			throw error;
		}
	}

	/**
	 * Get all organization roles (system-wide) with pagination
	 * @param start - Starting index (1-based)
	 * @param count - Number of roles to fetch
	 * @returns Array of system organization roles
	 */
	async getAllOrganizationRoles(
		start: number = 1,
		count: number = 10,
	): Promise<SystemOrganizationRole[]> {
		try {
			const response = await apiService.get<SystemOrganizationRole[]>(
				`/organization-roles/${start}/${count}`,
			);
			return SystemOrganizationRolesArray.parse(response);
		} catch (error) {
			console.error("Failed to fetch organization roles:", error);
			if (error instanceof z.ZodError) {
				throw new OrganizationApiError(
					"Invalid organization roles data received",
					error,
					undefined,
					400,
					error.issues,
				);
			}
			throw error;
		}
	}

	/**
	 * Get the total count of all organization roles (system-wide)
	 * @returns Total number of organization roles
	 */
	async getAllOrganizationRolesCount(): Promise<number> {
		try {
			const response = await apiService.get<number>(
				"/organization-roles/count",
			);
			const CountSchema = z.number().int().min(0);
			return CountSchema.parse(response);
		} catch (error) {
			console.error("Failed to fetch organization roles count:", error);
			if (error instanceof z.ZodError) {
				throw new OrganizationApiError(
					"Invalid organization roles count received",
					error,
					undefined,
					400,
					error.issues,
				);
			}
			throw error;
		}
	}

	// ==================== ORGANIZATION USER DATA ACCESS ====================

	/**
	 * Get data access entries for an organization user with pagination
	 * GET /organizations/{organizationId}/users/{organizationUserId}/data-access/{start}/{count}
	 *
	 * @param organizationId - The organization ID
	 * @param organizationUserId - The organization user ID (join table ID)
	 * @param start - Pagination start index
	 * @param count - Number of items to return
	 * @param params - Optional params
	 * @returns Array of DataAccessDisplay objects
	 */
	async getOrganizationUserDataAccess(
		organizationId: string,
		organizationUserId: string,
		start: number = 1,
		count: number = 10,
		params: DataAccessFilters = {},
	): Promise<DataAccessDisplay[]> {
		try {
			const response = await apiService.get<DataAccessDisplay[]>(
				`/organizations/${organizationId}/users/${organizationUserId}/data-access/${start}/${count}`,
				{ params },
			);

			return DataAccessDisplaysArray.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get data access for user ${organizationUserId} in organization ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get count of data access entries for an organization user
	 * GET /organizations/{organizationId}/users/{organizationUserId}/data-access/count
	 *
	 * @param organizationId - The organization ID
	 * @param organizationUserId - The organization user ID (join table ID)
	 * @param params - Optional params
	 * @returns Count of data access entries
	 */
	async getOrganizationUserDataAccessCount(
		organizationId: string,
		organizationUserId: string,
		params: DataAccessFilters = {},
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/organizations/${organizationId}/users/${organizationUserId}/data-access/count`,
				{ params },
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get data access count for user ${organizationUserId} in organization ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update data access entries for an organization user
	 * PUT /organizations/{organizationId}/users/{organizationUserId}/data-access
	 *
	 * @param organizationId - The organization ID
	 * @param organizationUserId - The organization user ID (join table ID)
	 * @param dataAccess - Array of DataAccessInfo objects to set
	 */
	async updateOrganizationUserDataAccess(
		organizationId: string,
		organizationUserId: string,
		dataAccess: DataAccessInfo[],
	): Promise<void> {
		try {
			const validatedData = DataAccessInfosArray.parse(dataAccess);

			await apiService.put(
				`/organizations/${organizationId}/users/${organizationUserId}/data-access`,
				validatedData,
			);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to update data access for user ${organizationUserId} in organization ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== ORGANIZATION DATA ACCESS ====================

	/**
	 * Get count of data access entries for an organization
	 * GET /organizations/{organizationId}/data-access/count
	 *
	 * @param organizationId - The organization ID
	 * @param params - Optional params
	 * @returns Count of data access entries
	 */
	async getOrganizationDataAccessCount(
		organizationId: string,
		params: DataAccessFilters = {},
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/organizations/${organizationId}/data-access/count`,
				{ params },
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get data access count for organization ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get data access entries for an organization with pagination
	 * GET /organizations/{organizationId}/data-access/{start}/{count}
	 *
	 * @param organizationId - The organization ID
	 * @param start - Pagination start index
	 * @param count - Number of items to return
	 * @param params - Optional params
	 * @returns Array of DataAccessDisplay objects
	 */
	async getOrganizationDataAccess(
		organizationId: string,
		start: number = 1,
		count: number = 10,
		params: DataAccessFilters = {},
	): Promise<DataAccessDisplay[]> {
		try {
			const response = await apiService.get<DataAccessDisplay[]>(
				`/organizations/${organizationId}/data-access/${start}/${count}`,
				{ params },
			);

			return DataAccessDisplaysArray.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get data access for organization ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get a single sub-organization by ID
	 * GET /organizations/{organizationId}/sub-organizations/{subOrganizationId}
	 *
	 * @param organizationId - The parent organization ID
	 * @param subOrganizationId - The sub-organization ID
	 * @returns SubOrganizationDisplay (single object per swagger spec)
	 */
	async getSubOrganization(
		organizationId: string,
		subOrganizationId: string,
	): Promise<SubOrganizationDisplay> {
		try {
			const response = await apiService.get<SubOrganizationDisplay>(
				`/organizations/${organizationId}/sub-organizations/${subOrganizationId}`,
			);

			return SubOrganizationDisplaySchema.parse(response);
		} catch (error) {
			if (error instanceof OrganizationApiError) {
				throw error;
			}
			throw new OrganizationApiError(
				`Failed to get sub-organization ${subOrganizationId} for organization ${organizationId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== GLOBAL ROLES ====================

	/**
	 * Get all global roles (both Platform and Organization scoped)
	 * GET /global-roles
	 *
	 * @returns Array of global roles
	 * @throws OrganizationApiError if API error occurs
	 */
	async getGlobalRoles(): Promise<GlobalRole[]> {
		try {
			const response = await apiService.get<GlobalRole[]>("/global-roles");
			return GlobalRolesArray.parse(response);
		} catch (error) {
			console.error("Failed to fetch global roles:", error);
			if (error instanceof z.ZodError) {
				throw new OrganizationApiError(
					"Invalid global roles data received",
					error,
					undefined,
					400,
					error.issues,
				);
			}
			throw error;
		}
	}

	// ==================== ORGANIZATION STATUSES ====================

	/**
	 * Get all organization statuses
	 * GET /organizations/statuses
	 *
	 * @returns Array of organization statuses
	 * @throws Error if API error
	 */
	async getOrganizationStatuses(): Promise<GetOrganizationStatusesResponse> {
		const response = await apiService.get<GetOrganizationStatusesResponse>(
			"/organizations/statuses",
		);
		return OrganizationStatusesArraySchema.parse(response);
	}

	// ==================== HEALTH CHECK OPERATIONS ====================

	/**
	 * Check API heartbeat
	 * GET /heartbeat
	 *
	 * @returns true if API is healthy, false otherwise
	 */
	async checkHeartbeat(): Promise<boolean> {
		try {
			await apiService.get("/heartbeat");
			return true;
		} catch (error) {
			console.error("Heartbeat check failed:", error);
			return false;
		}
	}

	/**
	 * Export organizations to CSV
	 * GET /organizations/{start}/{count}/export
	 *
	 * @param start - Starting index (1-based)
	 * @param count - Number of organizations to export
	 * @param params - Optional query parameters (same as getOrganizations)
	 * @returns Blob containing CSV data
	 * @throws Error if export fails
	 */
	async exportOrganizations(
		start: number,
		count: number,
		params?: OrganizationParams,
	): Promise<Blob> {
		const response = await apiService.get<Blob>(
			`/organizations/${start}/${count}/export`,
			{
				params,
				responseType: "blob",
				headers: {
					Accept: "text/csv",
				},
			},
		);
		return response;
	}

	async getOrganizationHistoryCount(organizationId: string): Promise<number> {
		const response = await apiService.get<number>(
			`/organizations/${encodeURIComponent(organizationId)}/history/count`,
		);
		return z.number().parse(response);
	}

	async getOrganizationHistory(
		organizationId: string,
		start: number,
		count: number,
	): Promise<AuditLogDisplay[]> {
		const response = await apiService.get<AuditLogDisplay[]>(
			`/organizations/${encodeURIComponent(organizationId)}/history/${start}/${count}`,
		);
		return AuditLogArray.parse(response);
	}

	// ==================== SUB-ORGANIZATION HISTORY ====================

	async getSubOrganizationHistoryCount(
		orgId: string,
		subOrgId: string,
	): Promise<number> {
		const response = await apiService.get<number>(
			`/sub-organizations/${subOrgId}/history/count`,
			{ headers: { "organization-id": orgId } },
		);
		return z.number().parse(response);
	}

	async getSubOrganizationHistory(
		orgId: string,
		subOrgId: string,
		start: number,
		count: number,
	): Promise<AuditLogDisplay[]> {
		const response = await apiService.get<AuditLogDisplay[]>(
			`/sub-organizations/${subOrgId}/history/${start}/${count}`,
			{ headers: { "organization-id": orgId } },
		);
		return AuditLogArray.parse(response);
	}

	// ==================== SUB-CONTRACTOR HISTORY ====================

	async getSubContractorHistoryCount(
		orgId: string,
		subConId: string,
	): Promise<number> {
		const response = await apiService.get<number>(
			`/sub-contractors/${subConId}/history/count`,
			{ headers: { "organization-id": orgId } },
		);
		return z.number().parse(response);
	}

	async getSubContractorHistory(
		orgId: string,
		subConId: string,
		start: number,
		count: number,
	): Promise<AuditLogDisplay[]> {
		const response = await apiService.get<AuditLogDisplay[]>(
			`/sub-contractors/${subConId}/history/${start}/${count}`,
			{ headers: { "organization-id": orgId } },
		);
		return AuditLogArray.parse(response);
	}

	/**
	 * Uploads a temporary file and returns the file id from the 201 body.
	 * POST /organizations/{organizationId}/files/temporary
	 */
	async uploadTemporaryOrganizationFile(
		organizationId: string,
		file: File,
	): Promise<string> {
		const formData = new FormData();
		formData.append("file", file, file.name);
		const response = await apiService.post<unknown>(
			`/organizations/${organizationId}/files/temporary`,
			formData,
			{
				headers: {
					"Content-Type": "multipart/form-data",
					...organizationIdHeader(organizationId),
				},
			},
		);
		const raw = typeof response === "string" ? response : String(response ?? "");
		return z.string().uuid().parse(raw.trim().replace(/^"|"$/g, ""));
	}
}

// Create and export a singleton instance
export const organizationService = new OrganizationService();
